import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import './style.css'
import { prefLabel, DAMPED_LABEL } from './pref'
import { ApiError, fetchRoute, formatDistance, formatDuration } from './api'
import type { RouteResponse } from './api'
import { clearRoute, drawRoute, fitToRoute, makeMarker, setHere } from './route-layer'
import { RouteProgress, formatRemaining } from './progress'
import type { Endpoint } from './route-layer'
import { createWhenControl } from './when'
import { createSearchBox } from './search'
import type { Place } from './search'
import { renderSunDial } from './sun'
import { addCoverage, fetchCities, fitToCoverage } from './coverage'
import type { City } from './coverage'
import { track } from './session'

// Belgrade centre — the pilot city (spec v0.2 §1.1). Used until geolocation lands.
const FALLBACK_CENTRE: [number, number] = [20.4573, 44.8154]

const map = new maplibregl.Map({
  container: 'map',
  style: 'https://tiles.openfreemap.org/styles/liberty',
  center: FALLBACK_CENTRE,
  zoom: 14,
  attributionControl: false,
})

map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')

// A basemap that fails to load is silent: MapLibre paints the style's
// background colour and everything else — route, pins, chips — keeps working,
// so the app looks fine while showing a blank page. Say something instead.
let mapErrorReported = false
map.on('error', (e) => {
  const message = String((e as { error?: Error }).error?.message ?? '')
  // Ignore the routine per-tile aborts MapLibre emits while panning.
  if (/abort/i.test(message)) return
  if (mapErrorReported) return
  mapErrorReported = true
  console.warn('map error:', message)
  setStatus('Map tiles unavailable — routing still works.')
})

const statusEl = document.getElementById('status') as HTMLParagraphElement
const prefEl = document.getElementById('pref') as HTMLInputElement
const prefTextEl = document.getElementById('prefText') as HTMLParagraphElement
const locateEl = document.getElementById('locate') as HTMLButtonElement
const sheetEl = document.getElementById('sheet') as HTMLElement
const chipsEl = document.getElementById('chips') as HTMLDivElement
const clearEl = document.getElementById('clear') as HTMLButtonElement
const demoEl = document.getElementById('demo') as HTMLButtonElement
const sunDialEl = document.getElementById('sunDial') as unknown as SVGElement
const sunTextEl = document.getElementById('sunText') as HTMLParagraphElement
const grabEl = document.getElementById('grab') as HTMLButtonElement

const aboutEl = document.getElementById('about') as HTMLDialogElement
document.getElementById('aboutBtn')?.addEventListener('click', () => {
  // A <video poster> is fetched even with preload="none", so the two posters
  // would cost ~200 kB on every visit for images only this dialog shows.
  // Attach them the first time it opens.
  for (const v of aboutEl.querySelectorAll<HTMLVideoElement>('video[data-poster]')) {
    v.poster = v.dataset.poster ?? ''
    delete v.dataset.poster
  }
  aboutEl.showModal()
})
// Tapping the backdrop closes it, which is what a phone user expects.
aboutEl.addEventListener('click', (e) => {
  if (e.target === aboutEl) aboutEl.close()
})

// Collapse the panel to give the map back. Starts collapsed on short screens,
// where the expanded panel would otherwise take most of the viewport.
function setCollapsed(collapsed: boolean) {
  sheetEl.classList.toggle('collapsed', collapsed)
  grabEl.setAttribute('aria-expanded', String(!collapsed))
  // Name what is hidden, not the mechanism — "Expand panel" told users nothing
  // about where the time and shade controls had gone.
  const label = document.getElementById('grabLabel')
  if (label) label.textContent = collapsed ? 'Time & shade options' : 'Hide options'
}
grabEl.addEventListener('click', () => setCollapsed(!sheetEl.classList.contains('collapsed')))
// Collapse on phone-width viewports regardless of height — a tall phone still
// wants the map, and height alone let a 839px screen start expanded at 62%.
setCollapsed(window.matchMedia('(max-width: 700px)').matches)

const when = createWhenControl(
  document.getElementById('hour') as HTMLInputElement,
  document.getElementById('date') as HTMLInputElement,
  document.getElementById('hourText') as HTMLOutputElement,
  document.getElementById('nowBtn') as HTMLButtonElement,
)
when.onChange(() => {
  renderSun()
  if (from && to) void requestRoute()
})

/** Sun compass — updates on every hour change, with or without a route. */
function renderSun() {
  renderSunDial(sunDialEl, sunTextEl, new Date(when.value()))
}

const fromSearch = createSearchBox(
  document.getElementById('fromInput') as HTMLInputElement,
  document.getElementById('fromResults') as HTMLUListElement,
  (p: Place) => {
    setEndpoint('from', [p.lng, p.lat])
    map.flyTo({ center: [p.lng, p.lat], zoom: 15 })
    if (to) void requestRoute()
  },
)

const toSearch = createSearchBox(
  document.getElementById('toInput') as HTMLInputElement,
  document.getElementById('toResults') as HTMLUListElement,
  (p: Place) => {
    setEndpoint('to', [p.lng, p.lat])
    if (from) void requestRoute()
    else map.flyTo({ center: [p.lng, p.lat], zoom: 15 })
  },
)

/** A known pair with a real shade choice, so the demo shows something. */
const DEMO: { from: [number, number]; to: [number, number]; fromName: string; toName: string } = {
  from: [20.4506, 44.8225],
  to: [20.4656, 44.8024],
  fromName: 'Kalemegdan',
  toName: 'Slavija',
}

demoEl.addEventListener('click', () => {
  setEndpoint('from', DEMO.from)
  setEndpoint('to', DEMO.to)
  fromSearch.setLabel(DEMO.fromName)
  toSearch.setLabel(DEMO.toName)
  clearEl.hidden = false
  void requestRoute()
})

/**
 * `hint` is guidance — what to do next — and is styled to match the input
 * fields. `detail` is diagnostics, which stays quiet so it never competes with
 * an instruction the user actually needs to read.
 */
const setStatus = (text: string, kind: 'hint' | 'detail' = 'hint') => {
  statusEl.textContent = text
  statusEl.classList.toggle('hint', kind === 'hint')
}

// ---- state -------------------------------------------------------------

let from: Endpoint | null = null
let to: Endpoint | null = null
let userPos: [number, number] | null = null
let inFlight: AbortController | null = null

// Live walking state. `progress` exists only while a route is on screen.
let progress: RouteProgress | null = null
let lastRoute: RouteResponse | null = null
let watchId: number | null = null

/** Covered cities, from the API. Empty until the first load resolves. */
let cities: City[] = []

/** Is a point inside any city we serve? */
function cityAt(lngLat: [number, number]): City | null {
  const [lng, lat] = lngLat
  return (
    cities.find(
      (c) => lat >= c.bbox[0] && lat <= c.bbox[2] && lng >= c.bbox[1] && lng <= c.bbox[3],
    ) ?? null
  )
}

/**
 * Say where the service does work, and show it.
 *
 * Telling someone "not here" while leaving them looking at the place it does
 * not work is half an answer; the map zooms out until every covered city is on
 * screen, which is also where the coverage overlay becomes visible.
 */
function showCoverage(message: string) {
  setStatus(message, 'hint')
  fitToCoverage(map, cities, sheetEl.offsetHeight)
}

/**
 * Follow the walker along the route.
 *
 * The point of the whole product is answering "how much further in the sun?"
 * while you are out in it, so this runs off `watchPosition` and recomputes
 * locally — no request per step, and it keeps working if the signal drops.
 */
function startWatching() {
  if (watchId !== null || !('geolocation' in navigator)) return
  watchId = navigator.geolocation.watchPosition(
    (pos) => {
      const here: [number, number] = [pos.coords.longitude, pos.coords.latitude]
      userPos = here
      showUser(here[0], here[1])
      renderProgress(here)
    },
    () => {
      /* a dropped fix is normal in a street canyon; keep the last one */
    },
    { enableHighAccuracy: true, maximumAge: 5_000, timeout: 20_000 },
  )
}

function stopWatching() {
  if (watchId !== null) {
    navigator.geolocation.clearWatch(watchId)
    watchId = null
  }
}

function renderProgress(here: [number, number]) {
  if (!progress || !lastRoute) return
  const p = progress.locate(here)
  if (!p) return

  // Far enough off the line and this is no longer "progress along a route".
  // Saying nothing beats confidently reporting the wrong remaining distance.
  const OFF_ROUTE_M = 60
  if (p.offRouteM > OFF_ROUTE_M) {
    setHere(map, null)
    setStatus(`${Math.round(p.offRouteM)} m off the route`, 'hint')
    drawRoute(map, lastRoute.geometry, lastRoute.segmentSun ?? [])
    return
  }

  // One "walk" per sitting, not one per GPS tick.
  track('walk', { city: lastRoute.city?.id, n: Math.round(p.remainingM) }, true)
  if (p.remainingM < 40) track('arrived', { city: lastRoute.city?.id }, true)
  setHere(map, p.snapped)
  const walkedUpTo = Math.round(p.fraction * ((lastRoute.segmentSun?.length ?? 1)))
  drawRoute(map, lastRoute.geometry, lastRoute.segmentSun ?? [], walkedUpTo)
  setStatus(formatRemaining(p), 'hint')
}

// ---- preference slider -------------------------------------------------
// The value is `p_user` in `p_eff = p_user × f_W` (§7.3). Weather damping is
// M3; until then the Worker passes it through unchanged.

function renderPref() {
  prefTextEl.textContent = prefLabel(Number(prefEl.value))
  prefTextEl.classList.remove('damped')
}
prefEl.addEventListener('input', renderPref)
// Re-route on release rather than on every pixel of drag.
prefEl.addEventListener('change', () => {
  if (from && to) void requestRoute()
})
renderPref()

// ---- routing -----------------------------------------------------------

function renderChips(r: RouteResponse) {
  const chips: string[] = [
    `<span class="chip"><b>${formatDistance(r.distanceM)}</b></span>`,
    `<span class="chip"><b>${formatDuration(r.durationS)}</b></span>`,
  ]

  // Climb is a headline number, not a footnote — it is half the product (§1.2).
  // Both directions are shown: a route that drops 90 m and climbs 80 m back is
  // worse for sweating than a flat one, and a single "climb" figure hides that.
  if (r.ascentM !== null && r.descentM !== null) {
    chips.push(
      `<span class="chip climb" title="Total ascent / descent along the route">` +
        `<b>↗ ${Math.round(r.ascentM)} m</b> <span class="sep">·</span> ` +
        `<b>↘ ${Math.round(r.descentM)} m</b></span>`,
    )
  } else {
    chips.push(`<span class="chip pending">↗ climb <i>M2</i></span>`)
  }

  if (r.shadeShare !== null) {
    // Never present synthetic numbers as measured ones.
    // "synthetic" is the headline while buildings are assumed, but the canopy
    // is now measured — say which, rather than tarring both with one word.
    const inputs = r.conditions.shadeInputs
    const synthetic = r.conditions.shadeSource === 'synthetic'
    const label = !synthetic
      ? ''
      : inputs?.canopy === 'measured'
        ? ' <i title="Real tree canopy; building heights still assumed">part real</i>'
        : ' <i>synthetic</i>'
    chips.push(
      `<span class="chip shade"><b>${Math.round(r.shadeShare * 100)}%</b> shade${label}</span>`,
    )
  } else {
    chips.push(`<span class="chip pending">shade <i>M2</i></span>`)
  }

  chipsEl.innerHTML = chips.join('')
  chipsEl.hidden = false
}

async function requestRoute() {
  if (!from || !to) return

  inFlight?.abort()
  const controller = new AbortController()
  inFlight = controller

  setStatus('Routing…', 'detail')
  try {
    const r = await fetchRoute(
      [from.lngLat[1], from.lngLat[0]],
      [to.lngLat[1], to.lngLat[0]],
      Number(prefEl.value),
      when.value(),
      controller.signal,
    )

    lastRoute = r
    progress = new RouteProgress(
      r.geometry.coordinates as [number, number][],
      r.segmentSun ?? [],
    )
    drawRoute(map, r.geometry, r.segmentSun ?? [])
    fitToRoute(map, r.geometry, sheetEl.offsetHeight)
    startWatching()
    renderChips(r)
    clearEl.hidden = false

    // §7.3: when the sun term is inert, say so instead of implying shade.
    // One state, several triggers — and name the trigger, don't guess at it.
    if (r.conditions.mode === 'least-effort') {
      prefTextEl.textContent =
        // Covers both ends of the day: 06:00 is not "after dark".
        r.conditions.reason === 'night'
          ? 'Sun too low — shortest way, least uphill.'
          : r.conditions.reason === 'out-of-season'
            ? 'Out of season — shortest way, least uphill.'
            : DAMPED_LABEL
      prefTextEl.classList.add('damped')
    } else {
      renderPref()
    }

    const bucket =
      r.conditions.hourBucket === null
        ? 'no sun'
        : `bucket ${r.conditions.hourBucket}`
    setStatus(
      `${r.settled.toLocaleString()} junctions in ${r.routeMs} ms · ${bucket}` +
        (r.conditions.seasonSet ? ` · ${r.conditions.seasonSet}` : ''),
      'detail',
    )
  } catch (err) {
    if (controller.signal.aborted) return
    clearRoute(map)
    chipsEl.hidden = true
    // Distinguish "the router said no" from "we never reached the router".
    if (err instanceof ApiError && (err.code === 'out_of_area' || err.code === 'cross_city')) {
      showCoverage(err.message)
      return
    }
    setStatus(
      err instanceof ApiError
        ? `No route: ${err.message}`
        : import.meta.env.DEV
          ? 'API unreachable — is `wrangler dev` running in ../worker?'
          : "Couldn't reach the router. Check your connection and try again.",
    )
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

function setEndpoint(kind: 'from' | 'to', lngLat: [number, number]) {
  const existing = kind === 'from' ? from : to
  existing?.marker.remove()

  const endpoint = makeMarker(map, lngLat, kind, (next) => {
    // Dragging a pin re-routes — the cheapest way to explore alternatives.
    if (kind === 'from') from!.lngLat = next
    else to!.lngLat = next
    void requestRoute()
  })

  if (kind === 'from') from = endpoint
  else to = endpoint
}

map.on('click', (e) => {
  const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat]

  const label = (ll: [number, number]) => `${ll[1].toFixed(4)}, ${ll[0].toFixed(4)}`

  // Check before setting anything. The first tap sends no request, so without
  // this someone outside coverage gets no answer until their second tap.
  if (cities.length && !cityAt(lngLat)) {
    // Say what to do, not only what went wrong. The map has just zoomed out to
    // the highlighted areas, so pointing at them is the actionable half.
    showCoverage('Outside the covered area — tap inside one of the highlighted squares.')
    return
  }

  if (!from) {
    setEndpoint('from', lngLat)
    fromSearch.setLabel(label(lngLat))
    setStatus('Now tap your destination.')
    clearEl.hidden = false
    return
  }
  const startCity = from ? cityAt(from.lngLat) : null
  const endCity = cityAt(lngLat)
  if (startCity && endCity && startCity.id !== endCity.id) {
    showCoverage(
      `${startCity.name} to ${endCity.name} — hladom plans walks within one city. ` +
        `Tap a destination inside the same square.`,
    )
    return
  }

  setEndpoint('to', lngLat)
  toSearch.setLabel(label(lngLat))
  void requestRoute()
})

function reset() {
  inFlight?.abort()
  stopWatching()
  progress = null
  lastRoute = null
  from?.marker.remove()
  to?.marker.remove()
  from = null
  to = null
  clearRoute(map)
  chipsEl.hidden = true
  clearEl.hidden = true
  renderPref()
  fromSearch.setLabel('')
  toSearch.setLabel('')
  setStatus(
    userPos ? 'Tap the map to set a destination.' : 'Tap the map to set a start, then a destination.',
  )
}

clearEl.addEventListener('click', reset)

// ---- geolocation -------------------------------------------------------

let userMarker: maplibregl.Marker | null = null

function showUser(lng: number, lat: number) {
  if (!userMarker) {
    const dot = document.createElement('div')
    dot.className = 'user-dot'
    userMarker = new maplibregl.Marker({ element: dot }).setLngLat([lng, lat]).addTo(map)
  } else {
    userMarker.setLngLat([lng, lat])
  }
}

function locate(fly = true) {
  if (!('geolocation' in navigator)) {
    setStatus('Tap the map to set a start, then a destination.')
    return
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { longitude, latitude } = pos.coords
      userPos = [longitude, latitude]
      track('located', {}, true)
      showUser(longitude, latitude)
      if (fly) map.flyTo({ center: [longitude, latitude], zoom: 15 })
      // Your position is the origin by default; one tap then gets you a route.
      if (!from) {
        setEndpoint('from', [longitude, latitude])
        fromSearch.setLabel('My location')
        setStatus('Tap the map to set a destination.')
      }
    },
    () => {
      setStatus('Location off — tap the map to set a start, then a destination.')
    },
    { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
  )
}

locateEl.addEventListener('click', () => locate(true))
map.on('load', () => {
  setStatus('Tap the map to set a start, then a destination.')
  track('land', {}, true)
  renderSun()
  locate(true)
  // Coverage areas come from the API, so adding a city needs no app change.
  void fetchCities()
    .then((list) => {
      cities = list
      addCoverage(map, list)
    })
    .catch(() => {
      /* the map is still useful without them */
    })
})

// Keep the locate button clear of the sheet however tall it renders.
const syncSheetHeight = () => {
  document.documentElement.style.setProperty('--sheet-height', `${sheetEl.offsetHeight}px`)
}
new ResizeObserver(syncSheetHeight).observe(sheetEl)
syncSheetHeight()

// ---- PWA ---------------------------------------------------------------

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    // The build id is in the URL deliberately.
    //
    // Cloudflare's zone-level Browser Cache TTL overrides the origin's
    // `max-age=0` on `.js` files: sw.js is served as max-age=0 from
    // pages.dev but as max-age=14400 through hladom.com. A browser could
    // therefore hold a stale service worker for four hours — and since the
    // service worker is what recovers a broken install, that is exactly the
    // wrong thing to cache.
    //
    // A per-build query string sidesteps it: each deploy registers a URL the
    // browser has never seen, so there is nothing to serve stale. Query
    // strings do not affect scope, which stays '/'.
    navigator.serviceWorker.register(`/sw.js?v=${__SW_VERSION__}`).catch(() => {
      /* offline support is a bonus; never block the app on it */
    })
  })
}

let deferredInstall: Event | null = null
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  deferredInstall = e
})
;(window as unknown as Record<string, unknown>).hladomInstall = () => {
  const evt = deferredInstall as (Event & { prompt?: () => void }) | null
  evt?.prompt?.()
  deferredInstall = null
}
